/**
 * The shell both review popups share: evidence on the left, the reviewer's decision on the
 * right, and a Previous / progress / Save-&-Next footer. Validation and audit differ only in
 * what fills the right pane, so the layout lives here once.
 */

import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import FieldEvidenceView from '../../shared/session/FieldEvidenceView';
import {
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  TEXT_COLOR_PRIMARY,
  TEXT_COLOR_SECONDARY,
} from '../../shared/cssConstants';

interface StepReviewModalProps {
  open: boolean;
  onClose: () => void;
  /** Fields whose transcript/notes evidence fills the left pane. */
  evidenceFields: string[];
  /** Right-pane heading — what the reviewer is doing, e.g. "Validation". */
  heading: string;
  /** Footer progress, e.g. "Fields Audited: 3 of 12". */
  progress: string;
  onPrevious: () => void;
  onNext: () => void;
  nextLabel: string;
  /** Disables navigation while a save is in flight. */
  busy: boolean;
  canGoBack: boolean;
  /** The decision controls for the current step. */
  children: React.ReactNode;
}

const StepReviewModal: React.FC<StepReviewModalProps> = ({
  open,
  onClose,
  evidenceFields,
  heading,
  progress,
  onPrevious,
  onNext,
  nextLabel,
  busy,
  canGoBack,
  children,
}) => (
  <Modal open={open} onClose={onClose} width="92vw" scrollContainer="modal" closeLabel="Close">
    <div
      style={{
        width: '100%',
        height: '68vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'hidden',
      }}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '16px', overflow: 'hidden' }}>
        <div
          style={{
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: '68%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          {evidenceFields.length > 0 && (
            <FieldEvidenceView key={evidenceFields.join('|')} fieldNames={evidenceFields} />
          )}
        </div>
        <div
          style={{
            flexGrow: 0,
            flexShrink: 1,
            flexBasis: '30%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'auto',
            borderLeft: '1px solid #e0e0e0',
            paddingLeft: '16px',
            paddingRight: '4px',
          }}
        >
          <div
            style={{
              fontSize: FONT_SIZE_BODY,
              fontWeight: 600,
              color: TEXT_COLOR_PRIMARY,
              marginBottom: '16px',
            }}
          >
            {heading}
          </div>
          {children}
        </div>
      </div>
    </div>
    <ModalFooter>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Button type="secondary" size="small" onClick={onPrevious} disabled={busy || !canGoBack}>
          ← Previous
        </Button>
        <span style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_SECONDARY }}>{progress}</span>
        <Button type="primary" size="small" onClick={onNext} disabled={busy}>
          {nextLabel}
        </Button>
      </div>
    </ModalFooter>
  </Modal>
);

export default StepReviewModal;
